import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex-grow bg-neutral-100" }
const _hoisted_2 = { class: "container mx-auto max-w-6xl" }
const _hoisted_3 = { class: "flex flex-col justify-between mt-8 mb-6 ml-6 sm:flex-row sm:items-center" }
const _hoisted_4 = { class: "text-3xl" }
const _hoisted_5 = { class: "bg-white shadow p-6 pb-10 rounded-t" }
const _hoisted_6 = {
  key: 1,
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "mb-3" }
const _hoisted_8 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_9 = { class: "font-semibold" }
const _hoisted_10 = { class: "font-semibold" }
const _hoisted_11 = { class: "flex flex-col lg:flex-row" }
const _hoisted_12 = { class: "shrink max-w-xl" }
const _hoisted_13 = {
  class: "mt-3 w-[500px] max-w-full sm:px-6 lg:mt-0",
  autocomplete: "off"
}

import axios, { Canceler } from 'axios';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import ApiErrorAlert from '@/components/ApiErrorAlert.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import MetaTags from '@/components/MetaTags.vue';
import Alert from '@/components/micro/AlertComponent.vue';
import CopyComponent from '@/components/micro/CopyComponent.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import RawRecords from '@/components/micro/RawRecords.vue';
import SkeletonComponent from '@/components/micro/SkeletonComponent.vue';
import { QueryResult, emptyDnsServer, emptyResponse } from '@/model/Api';
import { ApiErrorType, toErrorType } from '@/model/ApiErrorAlert';
import AdUnit from '@/components/ads/AdUnit.vue';
import { domainNameToUrlPath } from '@/common/domainName';
import { ReverseIpResults } from '@/common/adSlots';


export default /*@__PURE__*/_defineComponent({
  __name: 'ReverseIpLookupResults',
  props: {
  routerIp: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const router = useRouter();

const ip = ref('');
const domainPunycode = ref('');
const loading = ref(true);
const error = ref<ApiErrorType | null>(null);
const showSidebar = ref(window.innerWidth > 991);
const cancelToken = ref<Canceler | null>(null);
const queryResult = ref<QueryResult>({
  query: {
    server: emptyDnsServer,
    domain: ip.value,
    recordType: 'PTR',
  },
  response: emptyResponse,
});

function redirectTo(newIp: string): void {
  router.push(`/ip/${domainNameToUrlPath(newIp)}/domain-names/`);
}

function load() {
  loading.value = true;
  error.value = null;

  if (cancelToken.value) {
    cancelToken.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelToken.value = axiosSource.cancel;

  axios
    .post<QueryResult>('api/v1/ptr-records', {
      domain: ip.value,
      dnsServer: 'authoritative',
    }, {
      cancelToken: axiosSource.token,
    })
    .then((response) => {
      queryResult.value = response.data;
      loading.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        error.value = toErrorType(e.response.data.error);
      }
    });
}

watch(() => props.routerIp, () => {
  if (domainPunycode.value !== props.routerIp) {
    ip.value = decodeURI(props.routerIp)
      .toLowerCase();
    domainPunycode.value = decodeURI(props.routerIp)
      .toLowerCase();
    load();
  }
}, {
  immediate: true,
});

window.addEventListener('resize', () => {
  showSidebar.value = window.innerWidth > 991;
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      _createVNode(MetaTags, {
        title: `Reverse IP lookup for ${ip.value}`,
        description: `This is the canonical hostname for ${ip.value}. It has been configured by the owner of this IP address.`,
        url: `https://www.nslookup.io/ip/${domainPunycode.value}/domain-names/`
      }, null, 8, ["title", "description", "url"])
    ])),
    _createVNode(HeaderComponent, {
      "domain-search-to-url": (encodedDomain) => `/ip/${encodedDomain}/domain-names/`,
      "search-button-text": "Reverse lookup",
      domain: ip.value,
      "autocomplete-enabled": false
    }, null, 8, ["domain-search-to-url", "domain"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("main", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("h1", _hoisted_4, [
            _cache[0] || (_cache[0] = _createTextVNode("Reverse IP lookup for ", -1)),
            _createElementVNode("b", null, _toDisplayString(ip.value), 1)
          ]),
          _createVNode(DnsRecordsLink, {
            domain: ip.value,
            class: "shrink-0 hidden mr-6 sm:block xl:mr-0"
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createElementVNode("button", {
                type: "button",
                class: "inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              }, "All DNS records", -1)
            ]))]),
            _: 1
          }, 8, ["domain"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          (error.value !== null)
            ? (_openBlock(), _createBlock(ApiErrorAlert, {
                key: 0,
                error: error.value,
                domain: ip.value,
                dnsserver: "authoritative"
              }, null, 8, ["error", "domain"]))
            : (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createVNode(SkeletonComponent, { class: "w-56 h-8 mb-6" }),
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (n) => {
                    return _createElementVNode("div", { key: n }, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (n) => {
                        return _createVNode(SkeletonComponent, {
                          key: n,
                          class: "inline-block w-1/2 h-4 pr-[10%] bg-clip-content"
                        })
                      }), 64))
                    ])
                  }), 64)),
                  _createVNode(SkeletonComponent, { class: "inline-block w-56 h-8 my-6" }),
                  _createVNode(SkeletonComponent, { class: "w-24 h-4 mb-2" }),
                  _createVNode(SkeletonComponent, { class: "w-1/2 h-4 mb-6" }),
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (n) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: n }, [
                      _createVNode(SkeletonComponent, { class: "w-24 h-4 mb-2 block" }),
                      _createVNode(SkeletonComponent, { class: "w-1/2 h-4 mb-2 block" }),
                      _createVNode(SkeletonComponent, { class: "w-1/2 h-4 mb-6" })
                    ], 64))
                  }), 64))
                ]))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _createElementVNode("p", _hoisted_7, "Our DNS servers responded with these IP addresses when we queried it for the domain " + _toDisplayString(ip.value) + ". Some DNS servers may return different IP addresses based on your location.", 1),
                  (queryResult.value.response.answer.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(Alert, { type: "warning" }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, [
                              _createElementVNode("span", _hoisted_9, _toDisplayString(ip.value), 1),
                              _cache[2] || (_cache[2] = _createTextVNode(" does not have a reverse lookup record.", -1))
                            ])
                          ]),
                          _: 1
                        })
                      ]))
                    : (_openBlock(), _createBlock(Alert, {
                        key: 1,
                        type: "success",
                        class: "mt-4"
                      }, {
                        default: _withCtx(() => [
                          _cache[3] || (_cache[3] = _createTextVNode(" The canonical hostname of ", -1)),
                          _createElementVNode("span", _hoisted_10, _toDisplayString(ip.value), 1),
                          _cache[4] || (_cache[4] = _createTextVNode(" is ", -1)),
                          _createVNode(DnsRecordsLink, {
                            domain: queryResult.value.response.answer[0].record.target,
                            class: "font-semibold hover:underline"
                          }, null, 8, ["domain"]),
                          _createVNode(CopyComponent, {
                            content: queryResult.value.response.answer[0].record.target
                          }, null, 8, ["content"])
                        ]),
                        _: 1
                      })),
                  _createVNode(AdUnit, {
                    class: "mt-8 mx-auto",
                    config: _unref(ReverseIpResults)
                  }, null, 8, ["config"]),
                  _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "mt-8 mb-2 font-semibold text-2xl" }, "Question and response", -1)),
                  _createVNode(RawRecords, { records: queryResult.value }, null, 8, ["records"])
                ], 64)),
          _cache[9] || (_cache[9] = _createElementVNode("h2", { class: "mt-8 mb-2 font-semibold text-2xl" }, "Find the domain name for another IP address", -1)),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("p", _hoisted_12, [
              _cache[7] || (_cache[7] = _createTextVNode("With ", -1)),
              _createVNode(_component_router_link, {
                to: "/reverse-ip-lookup/",
                class: "text-blue-600 hover:text-blue-800 hover:underline"
              }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode("reverse IP lookup", -1)
                ]))]),
                _: 1
              }),
              _cache[8] || (_cache[8] = _createTextVNode(", you can find the domain name for any IP address. When you enter an IPv4 or IPv6 address in the input field below, it will show the canonical domain name for that IP address.", -1))
            ]),
            _createElementVNode("form", _hoisted_13, [
              _createVNode(DomainSearch, {
                "placeholder-text": "IP address",
                emphasize: "",
                buttonText: "Reverse lookup",
                prefill: ip.value,
                onSubmitted: redirectTo,
                "autocomplete-enabled": false
              }, null, 8, ["prefill"])
            ])
          ])
        ])
      ])
    ]),
    _createVNode(FooterComponent)
  ], 64))
}
}

})